import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ErrorModal, ErrorSeverity } from '../components/ErrorModal';
import { ServiceError } from '../services/ServiceError';

interface ErrorInfo {
  message: string;
  title?: string;
  severity?: ErrorSeverity;
  details?: string;
  onRetry?: () => void;
}

interface ErrorContextValue {
  showError: (error: ErrorInfo | string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const showError = useCallback((error: ErrorInfo | string | ServiceError) => {
    if (typeof error === 'string') {
      setErrorInfo({ message: error, severity: 'error' });
    } else if (error instanceof ServiceError) {
      // Handle ServiceError specifically
      setErrorInfo({
        message: error.message,
        title: error.code ? `Error: ${error.code}` : 'Error',
        severity: error.isRetryable ? 'warning' : 'error',
        details: error.stack
      });
    } else {
      setErrorInfo({ ...error, severity: error.severity || 'error' });
    }
    setIsOpen(true);
  }, []);

  const showWarning = useCallback((message: string, title?: string) => {
    setErrorInfo({ message, title, severity: 'warning' });
    setIsOpen(true);
  }, []);

  const showInfo = useCallback((message: string, title?: string) => {
    setErrorInfo({ message, title, severity: 'info' });
    setIsOpen(true);
  }, []);

  const clearError = useCallback(() => {
    setIsOpen(false);
    // Clear error info after animation completes
    setTimeout(() => setErrorInfo(null), 300);
  }, []);

  const handleRetry = () => {
    if (errorInfo?.onRetry) {
      clearError();
      errorInfo.onRetry();
    }
  };

  return (
    <ErrorContext.Provider value={{ showError, showWarning, showInfo, clearError }}>
      <span hidden data-testid="error-provider" />
      {children}
      {errorInfo && (
        <ErrorModal
          open={isOpen}
          onClose={clearError}
          title={errorInfo.title}
          message={errorInfo.message}
          severity={errorInfo.severity}
          details={errorInfo.details}
          onRetry={errorInfo.onRetry ? handleRetry : undefined}
        />
      )}
    </ErrorContext.Provider>
  );
};